/**
 * B.H. Copyright (c) 2026 Yemot HaMashiach Ltd.
 * All Rights Reserved.
 *
 * This software is the confidential and proprietary information of
 * Yemot HaMashiach Ltd. ("Confidential Information"). You shall not
 * disclose such Confidential Information and shall use it only in
 * accordance with the terms of the license agreement you entered into
 * with Yemot HaMashiach Ltd.
 *
 * Unauthorized copying of this file, via any medium, is strictly prohibited.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import cookie from "js-cookie";
import axios from "axios";
import { getLang } from "./Helper";

/**
 * הקאפצ'ה, בצד המסך.
 *
 * **המסך אינו מחליט דבר.** ההגדרה כולה מגיעה מ-`getResellerData` - האם פעילה, איזה
 * ספק, מפתח האתר ואילו פעולות מוגנות - והבדיקה עצמה נעשית בשרת על מה שנשלח. מה
 * שקורה כאן הוא הצגה והשגת טוקן, ותו לא; מסך שמדלג על הקאפצ'ה אינו עוקף דבר, הוא
 * פשוט מקבל סירוב.
 *
 * שתי הצורות שנתמכות, ושתיהן דרך אותה חתימה:
 *
 *   **v3** - בלי ממשק. הטוקן נוצר ברגע השליחה, קשור לפעולה, ומקבל ציון.
 *   **v2** - תיבת "אני לא רובוט" שהמשתמש מסמן, ושחייבת מקום על המסך.
 *
 * ההבדל ביניהן מסתכם ב-`node` שהוא `null` בראשונה, ולכן מסך אחד תומך בשתיהן בלי
 * להכיר אף אחת מהן.
 */

/** מזהה תגית הסקריפט, כדי שלא ייטען פעמיים בניווט בין מסכים. */
const SCRIPT_ELEMENT_ID = "yemot-captcha-provider";

/** ההגדרה כפי שהשרת מסר אותה, או `null` כשאין. */
export const captchaSettings = (resellerData) => {
  const settings = resellerData?.captcha;
  if (!settings || settings.active !== true || !settings.siteKey) {
    return null;
  }
  return settings;
};

/** האם הפעולה הזו דורשת קאפצ'ה. */
export const isCaptchaRequired = (resellerData, action) => {
  const settings = captchaSettings(resellerData);
  return !!settings && (settings.actions || []).includes(action);
};

/** שם הפעולה כפי שהשרת מצפה לראות אצל הספק. חייב להסכים עם `CaptchaGuard`. */
const providerActionName = (action) => `sso_${action}`;

const isCheckboxProvider = (settings) => settings?.provider === "recaptcha-v2";

/**
 * טוען את סקריפט הספק, פעם אחת לכל טעינת עמוד.
 *
 * ההבטחה נשמרת ברמת המודול ולא ברמת הרכיב: שני מסכים שנפתחים זה אחרי זה - וגם שני
 * שדות באותו מסך - אינם צריכים שני עותקים של אותו סקריפט.
 */
let providerScriptPromise = null;

const loadProviderScript = (settings) => {
  if (providerScriptPromise) {
    return providerScriptPromise;
  }

  providerScriptPromise = new Promise((resolve, reject) => {
    if (window.grecaptcha) {
      resolve(window.grecaptcha);
      return;
    }

    const script = document.createElement("script");
    script.id = SCRIPT_ELEMENT_ID;
    script.async = true;
    script.defer = true;
    // v2 נטען במפורש (`render=explicit`) כדי שנשלוט מתי ואיפה התיבה מצוירת;
    // v3 נטען עם המפתח, כי הוא אינו מצייר דבר.
    script.src = isCheckboxProvider(settings)
      ? `https://www.google.com/recaptcha/api.js?render=explicit&hl=${encodeURIComponent(getLang())}`
      : `https://www.google.com/recaptcha/api.js?render=${encodeURIComponent(settings.siteKey)}`;
    script.onload = () => resolve(window.grecaptcha);
    script.onerror = () => {
      // מאפסים כדי שמסך הבא ינסה שוב; רשת שנפלה לרגע אינה סיבה לוותר לתמיד.
      providerScriptPromise = null;
      reject(new Error("captcha_script_failed"));
    };
    document.head.appendChild(script);
  });

  return providerScriptPromise;
};

/** ממתין ל-`grecaptcha.ready`, שמובטח רק אחרי שהספריה סיימה להתאתחל. */
const whenProviderReady = (grecaptcha) =>
  new Promise((resolve) => {
    if (typeof grecaptcha?.ready === "function") {
      grecaptcha.ready(() => resolve(grecaptcha));
    } else {
      resolve(grecaptcha);
    }
  });

/* -------------------------------------------------------------------------- */

/**
 * הקאפצ'ה של מסך אחד.
 *
 * מחזיר שלושה דברים, ולא יותר:
 *
 *   `required` - האם הפעולה הזו מוגנת בכלל.
 *   `node`     - מה שצריך להיות על המסך. `null` ב-v3, התיבה ב-v2, ובשניהם גם שורת
 *                השגיאה, כדי שהמסך לא יצטרך לנהל אותה בעצמו.
 *   `attach`   - מקבל את גוף הבקשה ומחזיר אותו עם הטוקן, או `null` כשאי אפשר.
 *                `null` פירושו "אל תשלח" - ההסבר כבר מצויר ב-`node`.
 *
 * `attach` ולא "החזר לי טוקן": כך המסך אינו מחליט מתי לצרף ומתי לא, והפעולה
 * שהטוקן נוצר עבורה נקבעת במקום אחד. אין כאן `reset` נפרד - טוקן נוצל מנוקה בתוך
 * `attach` עצמו, ומסך שמחזיק פונקציה שאיש אינו קורא לה מטעה את מי שקורא אותו.
 */
export function useCaptcha(action) {
  const { t } = useTranslation();
  const resellerData = useSelector((state) => state.reseller.value);
  const settings = captchaSettings(resellerData);
  const required = isCaptchaRequired(resellerData, action);

  const [error, setError] = useState(null);
  const [widgetId, setWidgetId] = useState(null);
  const containerRef = useRef(null);
  const renderedRef = useRef(false);

  const checkbox = isCheckboxProvider(settings);

  /** טוען את הספק ברקע, כדי שלחיצה על השליחה לא תמתין להורדה. */
  useEffect(() => {
    if (!required) {
      return;
    }
    loadProviderScript(settings).catch(() => setError("captcha_unavailable"));
  }, [required, settings]);

  /** מצייר את התיבה, ורק ב-v2. `renderedRef` מונע ציור כפול בעדכון של הרכיב. */
  useEffect(() => {
    if (!required || !checkbox || renderedRef.current || !containerRef.current) {
      return;
    }
    let cancelled = false;
    loadProviderScript(settings)
      .then(whenProviderReady)
      .then((grecaptcha) => {
        if (cancelled || renderedRef.current || !containerRef.current) {
          return;
        }
        renderedRef.current = true;
        const id = grecaptcha.render(containerRef.current, {
          sitekey: settings.siteKey,
          callback: () => setError(null),
        });
        setWidgetId(id);
      })
      .catch(() => setError("captcha_unavailable"));
    return () => {
      cancelled = true;
    };
  }, [required, checkbox, settings]);

  const attach = useCallback(
    async (data) => {
      if (!required) {
        return data;
      }
      try {
        const grecaptcha = await whenProviderReady(await loadProviderScript(settings));

        if (checkbox) {
          const token = widgetId !== null ? grecaptcha.getResponse(widgetId) : "";
          if (!token) {
            // התיבה לא סומנה. זו אינה שגיאה של המערכת אלא שלב שלא הושלם.
            setError("captcha_not_checked");
            return null;
          }
          // חד-פעמי: הטוקן מנוקה מיד, כדי שלחיצה נוספת לא תגיש את אותו אחד שוב.
          grecaptcha.reset(widgetId);
          setError(null);
          return { ...data, captchaResponse: token };
        }

        const token = await grecaptcha.execute(settings.siteKey, {
          action: providerActionName(action),
        });
        setError(null);
        return { ...data, captchaResponse: token };
      } catch (e) {
        setError("captcha_unavailable");
        return null;
      }
    },
    [required, settings, checkbox, widgetId, action]
  );

  const node = required ? (
    <div className="captcha-area">
      {checkbox ? <div className="captcha-widget" ref={containerRef} /> : null}
      {error ? <h2 className="mt10 error">{t(error)}</h2> : null}
    </div>
  ) : null;

  return { required, node, attach };
}

/* -------------------------------------------------------------------------- */

/**
 * מה שקורה כשהשרת אומר "לא שלחת קאפצ'ה".
 *
 * זה מצב אמיתי ולא תיאורטי: הגדרות הריסיילר נשמרות אצל המבקר בעוגייה לחמש דקות,
 * ולכן ברגע שההגדרה נדלקת יש חלון שבו מסכים פתוחים עדיין אינם יודעים עליה. בלי
 * הטיפול הזה אותם מסכים היו נדחים שוב ושוב עד שהעוגייה תתיישן מעצמה - כלומר
 * ההדלקה עצמה הייתה תקלה.
 *
 * מוחקים את העוגייה ומרעננים את ההגדרות; הבקשה הבאה כבר נושאת טוקן. הריענון הוא
 * טעינה מחדש של העמוד ולא משיכה שקטה, כי הטופס צריך לצייר תיבה שלא הייתה בו
 * כשנטען, וגם לטעון את סקריפט הספק.
 */
export function installCaptchaRecovery() {
  axios.interceptors.response.use((response) => {
    if (response?.data?.message === "captcha_required") {
      cookie.remove("resellerData", { path: "/" });
      if (!sessionStorage.getItem("captchaReloaded")) {
        // פעם אחת בלבד. אם גם אחרי הריענון חסר טוקן, הבעיה אינה במטמון - וטעינה
        // חוזרת בלולאה הייתה הופכת אותה למסך שלא נפתח כלל.
        sessionStorage.setItem("captchaReloaded", "1");
        window.location.reload();
      }
    } else if (response?.data?.responseStatus === "OK") {
      sessionStorage.removeItem("captchaReloaded");
    }
    return response;
  });
}
